// CitationStylePicker — searchable combobox over the ~3k-entry CSL
// catalog with per-workspace favorites, recently-used, and a curated
// "Popular" group. Replaces the legacy 3-item <Select>.
//
// Behaviour:
//   • Closed state: a Button trigger showing the current style's label.
//   • Open state: cmdk Command in a Popover with an Input + scrollable
//     CommandList. Empty input shows Favorites → Recent → Popular.
//     Typing runs a debounced ILIKE search against csl_styles_catalog.
//   • Star icon on each row toggles favorite (writes csl_style_favorites).
//   • `extraTopOption` supports the "Workspace default (...)" sentinel
//     used by the per-Piece picker in BibliographyTabPanel.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  CITATION_STYLES,
  CITATION_STYLE_LABELS,
  getCitationStyleLabel,
  resolveCslStyleId,
} from "@/lib/citationStyles";
import {
  addFavorite,
  getCatalogEntries,
  listFavorites,
  listRecent,
  pushRecent,
  removeFavorite,
  searchCatalog,
  type CslCatalogEntry,
} from "@/lib/cslCatalog";

export type CitationStylePickerProps = {
  /** Canonical CSL style id; "" or null means "no selection". */
  value: string | null;
  /** Called with the canonical id when the user picks a style. */
  onChange: (styleId: string) => void;
  /** Workspace id — required for favorites + recents scoping. */
  workspaceId: string | null;
  disabled?: boolean;
  /** Width helper for the trigger; defaults to a settings-card width. */
  triggerClassName?: string;
  /**
   * Optional sentinel row pinned to the top (e.g. "Workspace default
   * (Chicago Notes-Bibliography)"). Selecting it calls onChange("").
   */
  extraTopOption?: {
    value: string; // sentinel id, e.g. "__workspace_default__"
    label: string;
  };
  /** Override the trigger label (used by the piece picker sentinel). */
  triggerLabel?: string;
};

export const CitationStylePicker = ({
  value,
  onChange,
  workspaceId,
  disabled,
  triggerClassName,
  extraTopOption,
  triggerLabel,
}: CitationStylePickerProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<CslCatalogEntry[]>([]);
  const [searching, setSearching] = useState(false);

  const [favorites, setFavorites] = useState<string[]>([]);
  const [recents, setRecents] = useState<string[]>([]);
  const [labelMap, setLabelMap] = useState<Record<string, string>>({});
  const [savingFav, setSavingFav] = useState(false);

  const isExtraSelected = !!extraTopOption && value === extraTopOption.value;
  const currentLabel =
    triggerLabel ??
    (isExtraSelected
      ? extraTopOption!.label
      : value
        ? getCitationStyleLabel(value, labelMap[resolveCslStyleId(value)] ?? null)
        : "Select citation style…");

  // ---- Load favorites + recents + labels on open ----
  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;
    void (async () => {
      try {
        const [favs, fetched] = await Promise.all([
          listFavorites(workspaceId),
          getCatalogEntries(
            Array.from(
              new Set([
                ...CITATION_STYLES,
                ...listRecent(workspaceId),
                ...(value ? [resolveCslStyleId(value)] : []),
              ]),
            ),
          ),
        ]);
        if (cancelled) return;
        setFavorites(favs.map((f) => f.styleId));
        setRecents(listRecent(workspaceId));
        const map: Record<string, string> = {};
        fetched.forEach((e) => {
          map[e.id] = e.shortTitle ?? e.title;
        });
        setLabelMap((prev) => ({ ...prev, ...map }));

        // Fetch labels for favorites too if not already in CITATION_STYLES.
        const missing = favs
          .map((f) => f.styleId)
          .filter((id) => !map[id] && !CITATION_STYLE_LABELS[id]);
        if (missing.length > 0) {
          const more = await getCatalogEntries(missing);
          if (cancelled) return;
          const extra: Record<string, string> = {};
          more.forEach((e) => {
            extra[e.id] = e.shortTitle ?? e.title;
          });
          setLabelMap((prev) => ({ ...prev, ...extra }));
        }
      } catch (e) {
        toast.error((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, value]);

  // ---- Debounced catalog search ----
  const debounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    const term = search.trim();
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!term) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(() => {
      void (async () => {
        try {
          const rows = await searchCatalog(term, { limit: 80 });
          setSearchResults(rows);
          const extra: Record<string, string> = {};
          rows.forEach((e) => {
            extra[e.id] = e.shortTitle ?? e.title;
          });
          setLabelMap((prev) => ({ ...prev, ...extra }));
        } catch (e) {
          toast.error((e as Error).message);
        } finally {
          setSearching(false);
        }
      })();
    }, 180);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [search, open]);

  const handlePick = useCallback(
    (id: string) => {
      if (extraTopOption && id === extraTopOption.value) {
        onChange(extraTopOption.value);
      } else {
        const canonical = resolveCslStyleId(id);
        onChange(canonical);
        if (workspaceId) pushRecent(workspaceId, canonical);
      }
      setOpen(false);
      setSearch("");
    },
    [extraTopOption, onChange, workspaceId],
  );

  const toggleFavorite = useCallback(
    async (id: string, currentlyFav: boolean) => {
      if (!workspaceId) return;
      setSavingFav(true);
      try {
        if (currentlyFav) {
          await removeFavorite(workspaceId, id);
          setFavorites((prev) => prev.filter((s) => s !== id));
        } else {
          await addFavorite(workspaceId, id);
          setFavorites((prev) => [...prev, id]);
        }
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setSavingFav(false);
      }
    },
    [workspaceId],
  );

  const renderRow = (id: string, label: string) => {
    const isFav = favorites.includes(id);
    const isSel = !isExtraSelected && resolveCslStyleId(value ?? "") === id;
    return (
      <CommandItem
        key={id}
        value={`${id} ${label}`}
        onSelect={() => handlePick(id)}
        className="group flex items-center gap-2"
      >
        <Check className={cn("h-3.5 w-3.5", isSel ? "opacity-100" : "opacity-0")} />
        <span className="flex-1 truncate">{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void toggleFavorite(id, isFav);
              }}
              disabled={savingFav || !workspaceId}
              aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
              className="rounded p-0.5 opacity-60 hover:opacity-100 disabled:opacity-30"
            >
              <Star
                className={cn(
                  "h-3.5 w-3.5",
                  isFav ? "fill-current text-amber-500" : "text-muted-foreground",
                )}
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">
            {isFav ? "Remove from favorites" : "Add to favorites"}
          </TooltipContent>
        </Tooltip>
      </CommandItem>
    );
  };

  const labelFor = (id: string) =>
    getCitationStyleLabel(id, labelMap[id] ?? null);

  const searchVisible = search.trim().length > 0;
  const popularIds = useMemo(
    () => CITATION_STYLES.filter((id) => !favorites.includes(id)),
    [favorites],
  );
  const recentIds = useMemo(
    () => recents.filter((id) => !favorites.includes(id) && !popularIds.includes(id)),
    [recents, favorites, popularIds],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("justify-between", triggerClassName)}
        >
          <span className="truncate">{currentLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search 3,000+ citation styles…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {searching ? (
              <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Searching…
              </div>
            ) : null}

            {!searchVisible && extraTopOption ? (
              <CommandGroup>
                <CommandItem
                  value={extraTopOption.value}
                  onSelect={() => handlePick(extraTopOption.value)}
                  className="flex items-center gap-2"
                >
                  <Check
                    className={cn(
                      "h-3.5 w-3.5",
                      isExtraSelected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate italic text-muted-foreground">
                    {extraTopOption.label}
                  </span>
                </CommandItem>
              </CommandGroup>
            ) : null}

            {!searchVisible && favorites.length > 0 ? (
              <>
                <CommandGroup heading="Favorites">
                  {favorites.map((id) => renderRow(id, labelFor(id)))}
                </CommandGroup>
                <CommandSeparator />
              </>
            ) : null}

            {!searchVisible && recentIds.length > 0 ? (
              <>
                <CommandGroup heading="Recent">
                  {recentIds.map((id) => renderRow(id, labelFor(id)))}
                </CommandGroup>
                <CommandSeparator />
              </>
            ) : null}

            {!searchVisible ? (
              <CommandGroup heading="Popular">
                {popularIds.map((id) => renderRow(id, labelFor(id)))}
              </CommandGroup>
            ) : null}

            {searchVisible ? (
              searchResults.length === 0 && !searching ? (
                <CommandEmpty>No styles matched.</CommandEmpty>
              ) : (
                <CommandGroup heading={`Results (${searchResults.length})`}>
                  {searchResults.map((r) =>
                    renderRow(r.id, r.shortTitle ?? r.title),
                  )}
                </CommandGroup>
              )
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
